import { Image } from '@tiptap/extension-image';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Editor } from '@tiptap/react';

const BACKEND_URI = import.meta.env.VITE_API_BASE_URL || '';

/**
 * Upload image to Azure Blob Storage
 *
 * @param file - Image file to upload
 * @param token - Authentication token
 * @returns Promise with image URL
 */
async function uploadImageToAzure(file: File, token: string): Promise<string> {
    const formData = new FormData();
    formData.append('file', file);

    try {
        const response = await fetch(`${BACKEND_URI}/api/vector_files/upload`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
            },
            body: formData,
        });

        if (!response.ok) {
            throw new Error(`Upload failed: ${response.statusText}`);
        }

        const data = await response.json();

        // Assuming the API returns the file URL in the response
        // Adjust this based on your actual API response structure
        return data.url || data.file_url || data.filename;
    } catch (error) {
        console.error('Image upload failed:', error);
        throw error;
    }
}

/**
 * Handle image paste/drop events
 */
function handleImageFiles(files: File[], editor: Editor, token: string) {
    files.forEach(async (file) => {
        // Only process image files
        if (!file.type.startsWith('image/')) {
            return;
        }

        // Create a temporary placeholder
        const tempSrc = URL.createObjectURL(file);

        // Insert placeholder image immediately for better UX
        editor.chain().focus().setImage({ src: tempSrc }).run();

        try {
            // Upload the actual file
            const url = await uploadImageToAzure(file, token);

            // Replace placeholder with actual URL
            const { state } = editor;
            const { doc, tr } = state;

            doc.descendants((node, pos) => {
                if (node.type.name === 'image' && node.attrs.src === tempSrc) {
                    tr.setNodeMarkup(pos, undefined, {
                        ...node.attrs,
                        src: url,
                    });
                }
            });

            editor.view.dispatch(tr);

            // Revoke the temporary URL
            URL.revokeObjectURL(tempSrc);
        } catch (error) {
            console.error('Image upload failed:', error);
            // Remove the placeholder image on error
            const { state } = editor;
            const { doc, tr } = state;

            doc.descendants((node, pos) => {
                if (node.type.name === 'image' && node.attrs.src === tempSrc) {
                    tr.delete(pos, pos + node.nodeSize);
                }
            });

            editor.view.dispatch(tr);
            URL.revokeObjectURL(tempSrc);

            // Optionally show error to user
            alert('Image upload failed. Please try again.');
        }
    });
}

/**
 * Custom Image Extension with Azure Blob Storage upload support
 */
export const ImageUpload = (token: string) => Image.extend({
    addProseMirrorPlugins() {
        const editor = this.editor;

        return [
            new Plugin({
                key: new PluginKey('imageUpload'),
                props: {
                    // Handle paste events
                    handlePaste(view, event) {
                        const items = Array.from(event.clipboardData?.items || []);
                        const files = items
                            .filter((item) => item.type.startsWith('image/'))
                            .map((item) => item.getAsFile())
                            .filter((file): file is File => file !== null);

                        if (files.length > 0) {
                            event.preventDefault();
                            handleImageFiles(files, editor, token);
                            return true;
                        }

                        return false;
                    },

                    // Handle drop events
                    handleDrop(view, event) {
                        const files = Array.from(event.dataTransfer?.files || [])
                            .filter((file) => file.type.startsWith('image/'));

                        if (files.length > 0) {
                            event.preventDefault();

                            // Get the position where the drop occurred
                            const coordinates = view.posAtCoords({
                                left: event.clientX,
                                top: event.clientY,
                            });

                            if (coordinates) {
                                // Move cursor to drop position
                                const tr = view.state.tr.setSelection(
                                    view.state.schema.text.createAndFill(null)
                                        ? view.state.schema.selection.near(view.state.doc.resolve(coordinates.pos))
                                        : view.state.selection
                                );
                                view.dispatch(tr);
                            }

                            handleImageFiles(files, editor, token);
                            return true;
                        }

                        return false;
                    },
                },
            }),
        ];
    },
}).configure({
    inline: false,
    allowBase64: true,
    HTMLAttributes: {
        class: 'tiptap-image',
    },
});
